import { Bullet, Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class GroupedColumnChart extends BaseChart {
  xAxis!: CategoryAxis<AxisRenderer> | DateAxis<AxisRenderer>;
  yAxisPrimary!: ValueAxis<AxisRendererY>;
  yAxisSecondary!: ValueAxis<AxisRendererY>;
  isDateAxis!: boolean;
  interval!: time.ITimeInterval;

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    const series = [this.settings.yAxisPrimary, this.settings.yAxisSecondary];

    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.xAxis) as time.ITimeInterval;
    }

    this.createChart();
    this.createAxes();
    this.createCursor();

    series.forEach((name, index) => {
      this.createSeries(name, index);
    });

    this.showLegend(series, (this.chart as XYChart).series.values as LegendData);
  }

  createSeries(name: string, index: number) {
    const s: ColumnSeries = this.isDateAxis
      ? (this.chart as XYChart).series.push(
          ColumnSeries.new(this.root, {
            name: name,
            yAxis: index ? this.yAxisSecondary : this.yAxisPrimary,
            xAxis: this.xAxis,
            valueYField: name,
            valueXField: this.settings.xAxis,
            fill: this.getSeriesColor(index),
          })
        )
      : (this.chart as XYChart).series.push(
          ColumnSeries.new(this.root, {
            name: name,
            yAxis: index ? this.yAxisSecondary : this.yAxisPrimary,
            xAxis: this.xAxis,
            valueYField: name,
            categoryXField: this.settings.xAxis,
            fill: this.getSeriesColor(index),
          })
        );

    this.createTooltip(s as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = s.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        const xField = this.isDateAxis ? s.get("valueXField") : s.get("categoryXField");
        forIn(tooltipValues, (value, key) => {
          if (xField === key || s.get("valueYField") === key) {
            param.push({ key, value });
          }
        });
      }

      return param;
    });

    if (this.settings.showDataLabels) {
      const metricFormatter = this.formatters?.[name];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);
      const axis = s.get("yAxis") as ValueAxis<AxisRendererY>;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.15;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      s.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -32,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueY");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueY");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, {
          locationY: 1,
          sprite: label,
        });
      });
    }

    this.createLegendLabel(s, name);
    this.updateSeriesData(name, s);
  }

  createChart() {
    const chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: false,
        wheelY: "zoomX",
        layout: this.layout,
      })
    );

    this.chart = chart;
  }

  createCursor() {
    const cursor = (this.chart as XYChart).set("cursor", XYCursor.new(this.root, {}));
    cursor.lineY.set("forceHidden", true);
    cursor.lineX.set("forceHidden", true);
  }

  createAxes() {
    const yRendererPrimary = AxisRendererY.new(this.root, { cellStartLocation: 0.1, cellEndLocation: 0.9 });
    yRendererPrimary.labels.template.setAll({ ...chartFontSettings.label });

    this.yAxisPrimary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: yRendererPrimary,
        min: 0,
        marginRight: 14,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisPrimary, this.data),
      })
    );

    const yRendererSecondary = AxisRendererY.new(this.root, {
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      opposite: true,
    });
    yRendererSecondary.labels.template.setAll({ ...chartFontSettings.label });

    this.yAxisSecondary = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: yRendererSecondary,
        min: 0,
        marginLeft: 14,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxisSecondary, this.data),
      })
    );

    const xRenderer = AxisRendererX.new(this.root, {});
    xRenderer.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.xAxis = this.isDateAxis
      ? (this.chart as XYChart).xAxes.push(
          DateAxis.new(this.root, {
            renderer: xRenderer,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.xAxis),
            marginTop: 14,
          })
        )
      : (this.chart as XYChart).xAxes.push(
          CategoryAxis.new(this.root, {
            categoryField: this.settings.xAxis,
            renderer: xRenderer,
            marginTop: 14,
          })
        );

    if (
      this.module === PLAN_MODULE &&
      !this.settings.useExploreData &&
      this.isDateAxis &&
      this.settings.showDataLabels
    ) {
      const xAxis = this.xAxis as DateAxis<AxisRendererX>;
      if (this.settings.xAxis === "week") {
        this.root.events.once("frameended", () => {
          const privateMin = xAxis.getPrivate("min");
          const privateMax = xAxis.getPrivate("max");
          const privateStep = xAxis.getPrivate("step");
          if (
            typeof privateMin === "number" &&
            typeof privateMax === "number" &&
            typeof privateStep === "number" &&
            privateStep > 0
          ) {
            xAxis.set("min", privateMin - privateStep);
            xAxis.set("max", privateMax + privateStep);
            xAxis.set("strictMinMax", true);
          }
        });
      } else if (this.settings.xAxis === "month" || this.settings.xAxis === "quarter") {
        const currentExtraMin = Number(xAxis.get("extraMin") ?? 0);
        const currentExtraMax = Number(xAxis.get("extraMax") ?? 0);
        const requiredExtra = 0.03;
        if (currentExtraMin < requiredExtra) {
          xAxis.set("extraMin", requiredExtra);
        }
        if (currentExtraMax < requiredExtra) {
          xAxis.set("extraMax", requiredExtra);
        }
      }

      if (this.settings.xAxis === "week" || this.settings.xAxis === "quarter") {
        this.root.events.once("frameended", () => {
          const privateMaxPrimary = this.yAxisPrimary.getPrivate("max");
          const privateStepPrimary = this.yAxisPrimary.getPrivate("step");
          if (
            typeof privateMaxPrimary === "number" &&
            typeof privateStepPrimary === "number" &&
            privateStepPrimary > 0
          ) {
            this.yAxisPrimary.set("max", privateMaxPrimary + privateStepPrimary);
            this.yAxisPrimary.set("strictMinMax", true);
          }

          const privateMaxSecondary = this.yAxisSecondary.getPrivate("max");
          const privateStepSecondary = this.yAxisSecondary.getPrivate("step");
          if (
            typeof privateMaxSecondary === "number" &&
            typeof privateStepSecondary === "number" &&
            privateStepSecondary > 0
          ) {
            this.yAxisSecondary.set("max", privateMaxSecondary + privateStepSecondary);
            this.yAxisSecondary.set("strictMinMax", true);
          }
        });
      }
    }

    if (!this.isDateAxis) {
      const xRenderer = (this.xAxis as CategoryAxis<AxisRendererX>).get("renderer") as AxisRendererX;
      xRenderer.set("minGridDistance", 1);

      const getCategoryCellWidth = () => {
        const plotWidth = (this.chart as XYChart).plotContainer.getPrivate("width") ?? 0;
        const count = this.xAxis?.dataItems?.length ?? 0;
        return count > 0 ? plotWidth / count : plotWidth;
      };

      const updateXAxisLabelsLayout = () => {
        const cell = getCategoryCellWidth();
        const padding = 16;
        const maxWidth = Math.max(30, Math.floor(cell - padding));

        const behavior = cell < 70 ? "truncate" : "wrap";

        xRenderer.labels.template.setAll({
          ...chartFontSettings.label,
          textAlign: "center",
          rotation: 0,
          centerX: p50,
          centerY: 0.5,
          maxWidth,
          oversizedBehavior: behavior,
          tooltipText: `{${this.settings.xAxis}}`,
          visible: true,
        });
      };

      (this.chart as XYChart).plotContainer.onPrivate("width", updateXAxisLabelsLayout);
      (this.chart as XYChart).plotContainer.onPrivate("height", updateXAxisLabelsLayout);

      (this.xAxis as CategoryAxis<AxisRendererX>).events.on("datavalidated", updateXAxisLabelsLayout);

      updateXAxisLabelsLayout();
    }

    if (this.settings.yAxisPrimaryTitle) {
      this.yAxisPrimary.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisPrimaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: percent(50),
          marginRight: 10,
        })
      );
    }

    if (this.settings.yAxisSecondaryTitle) {
      this.yAxisSecondary.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisSecondaryTitle,
          ...chartFontSettings.axisTitle,
          rotation: 90,
          y: percent(50),
          centerX: percent(50),
          marginLeft: 10,
        })
      );
    }

    if (this.settings.xAxisTitle) {
      this.xAxis.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: percent(50),
          marginTop: axisTitle.marginTop,
          paddingBottom: 0,
          centerX: p50,
        })
      );
    }

    this.xAxis.data.setAll(this.data);
  }

  mapData(data: ChartDataRecord[]) {
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      (this.module === PLAN_MODULE || this.isDualMode) &&
      (!this.settings?.useExploreData || this.isDualMode) &&
      this.chartFamily !== FAMILY.OVERTIME &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.xAxis;
      const displayMetric = this.settings.yAxisPrimary;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    preparedData = preparedData.map((x) => ({
      [this.settings.xAxis]: x[this.settings.xAxis],
      [this.settings.yAxisPrimary]: x[this.settings.yAxisPrimary] ?? 0,
      [this.settings.yAxisSecondary]: x[this.settings.yAxisSecondary] ?? 0,
    }));
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;

    if (!this.isDateAxis) {
      preparedData = orderBy(
        preparedData,
        [this.settings.yAxisPrimary, this.settings.yAxisSecondary],
        ["desc", "desc"]
      ) as ChartDataRecord[];

      preparedData = this.showItems(preparedData as ChartDataRecord[], this.settings.xAxis, this.settings.yAxisPrimary);
    }

    return preparedData;
  }

  updateSeriesData(seriesName: string, series: ColumnSeries): void {
    const data = this.isDateAxis
      ? this.data.map((el: ChartDataRecord) => ({
          [this.settings.xAxis]: stringOrNumberToDate(el[this.settings.xAxis] as string).getTime(),
          [seriesName]: el[seriesName],
        }))
      : this.data;

    series.data.setAll(data);
  }
}
